import { Epic, StateObservable } from 'redux-observable';
import { of, from } from 'rxjs';
import { mergeMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';
import { RootState } from '../store';
import { 
  fetchOverallPerformance, 
  fetchLOBPerformance, 
  fetchChannelPerformanceBreakdown, 
  fetchSubchannelPerformanceBreakdown, 
  fetchProductPerformanceBreakdown,
  fetchProductChannelDistribution,
  fetchChannelProductDistribution,
  fetchProductStatusDistribution,
  fetchChannelStatusDistribution,
  fetchSubchannelStatusDistribution,
  fetchProductConversionRate,
  fetchChannelConversionRate,
  fetchSubchannelConversionRate,
  ProductStatusResponse,
  ChannelStatusResponse,
  SubchannelStatusResponse,
  ProductConversionResponse,
  ChannelConversionResponse,
  SubchannelConversionResponse
} from '@/services/api';
import {
  setOverallPerformance,
  setLOBPerformance,
  setLoading,
  setError,
  setSelectedRegion,
  resetMatrixStates,
  FETCH_CHANNEL_PERFORMANCE_BREAKDOWN_START,
  fetchChannelPerformanceBreakdownSuccess,
  fetchChannelPerformanceBreakdownFailure,
  setChannelPerformanceBreakdown,
  setChannelPerformanceBreakdownLoading,
  setChannelPerformanceBreakdownError,
  FETCH_SUBCHANNEL_PERFORMANCE_BREAKDOWN_START,
  FETCH_SUBCHANNEL_PERFORMANCE_BREAKDOWN_SUCCESS,
  FETCH_SUBCHANNEL_PERFORMANCE_BREAKDOWN_FAILURE,
  fetchSubchannelPerformanceBreakdownSuccess,
  fetchSubchannelPerformanceBreakdownFailure,
  setSubchannelPerformanceBreakdown,
  setSubchannelPerformanceBreakdownLoading,
  setSubchannelPerformanceBreakdownError,
  FETCH_PRODUCT_PERFORMANCE_BREAKDOWN_START,
  FETCH_PRODUCT_PERFORMANCE_BREAKDOWN_SUCCESS,
  FETCH_PRODUCT_PERFORMANCE_BREAKDOWN_FAILURE,
  fetchProductPerformanceBreakdownSuccess,
  fetchProductPerformanceBreakdownFailure,
  setProductPerformanceBreakdown,
  setProductPerformanceBreakdownLoading,
  setProductPerformanceBreakdownError,
  FETCH_PRODUCT_CHANNEL_DISTRIBUTION_START,
  fetchProductChannelDistributionSuccess,
  fetchProductChannelDistributionFailure,
  setProductChannelDistribution,
  setProductChannelDistributionLoading,
  setProductChannelDistributionError,
  FETCH_CHANNEL_PRODUCT_DISTRIBUTION_START,
  fetchChannelProductDistributionSuccess,
  fetchChannelProductDistributionFailure,
  setChannelProductDistribution,
  setChannelProductDistributionLoading,
  setChannelProductDistributionError,
  ProductChannelDistributionResponse,
  ChannelProductDistributionResponse,
  FETCH_PRODUCT_STATUS_DISTRIBUTION_START,
  fetchProductStatusDistributionSuccess,
  fetchProductStatusDistributionFailure,
  setProductStatusDistribution,
  setProductStatusDistributionLoading,
  setProductStatusDistributionError,
  FETCH_CHANNEL_STATUS_DISTRIBUTION_START,
  fetchChannelStatusDistributionSuccess,
  fetchChannelStatusDistributionFailure,
  setChannelStatusDistribution,
  setChannelStatusDistributionLoading,
  setChannelStatusDistributionError,
  FETCH_SUBCHANNEL_STATUS_DISTRIBUTION_START,
  fetchSubchannelStatusDistributionSuccess,
  fetchSubchannelStatusDistributionFailure,
  setSubchannelStatusDistribution,
  setSubchannelStatusDistributionLoading,
  setSubchannelStatusDistributionError,
  FETCH_PRODUCT_CONVERSION_RATE_START,
  fetchProductConversionRateSuccess,
  fetchProductConversionRateFailure,
  setProductConversionRate,
  setProductConversionRateLoading,
  setProductConversionRateError,
  FETCH_CHANNEL_CONVERSION_RATE_START,
  fetchChannelConversionRateSuccess,
  fetchChannelConversionRateFailure,
  setChannelConversionRate,
  setChannelConversionRateLoading,
  setChannelConversionRateError,
  FETCH_SUBCHANNEL_CONVERSION_RATE_START,
  fetchSubchannelConversionRateSuccess,
  fetchSubchannelConversionRateFailure,
  setSubchannelConversionRate,
  setSubchannelConversionRateLoading,
  setSubchannelConversionRateError
} from '../slices/dashboardSlice';
import { setCurrentFilters } from '../slices/filtersSlice';
import { combineEpics } from 'redux-observable';

// Action Types
export const FETCH_PERFORMANCE_DATA = 'dashboard/fetchPerformanceData';
export const FETCH_PERFORMANCE_DATA_SUCCESS = 'dashboard/fetchPerformanceDataSuccess';
export const FETCH_PERFORMANCE_DATA_FAILURE = 'dashboard/fetchPerformanceDataFailure';

// Action Creators
export const fetchPerformanceData = (filters?: any) => ({ type: FETCH_PERFORMANCE_DATA, payload: filters });
export const fetchPerformanceDataSuccess = (data: any) => ({ type: FETCH_PERFORMANCE_DATA_SUCCESS, payload: data });
export const fetchPerformanceDataFailure = (error: string) => ({ type: FETCH_PERFORMANCE_DATA_FAILURE, payload: error });

// Epic
export const fetchPerformanceDataEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    mergeMap(action => {
      if (action.type !== FETCH_PERFORMANCE_DATA) return of();
      
      // Get filters from action payload or use empty object
      const actionFilters = action.payload || {};
      
      // Get current state to extract selectedRegion if not in filters
      const state = state$.value;
      const selectedRegion = state.dashboard.selectedRegion;
      
      // Combine filters with selectedRegion if not already present
      const filters = {
        ...actionFilters,
        selectedRegion: actionFilters.selectedRegion || selectedRegion
      };
      
      return Promise.all([
        fetchOverallPerformance(true, filters),
        fetchLOBPerformance(true, filters)
      ]).then(([overallResponse, lobResponse]) => {
        return fetchPerformanceDataSuccess({ overallResponse, lobResponse });
      }).catch(error => {
        return fetchPerformanceDataFailure(error.message);
      });
    })
  );

// Handle success
export const handlePerformanceDataSuccessEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    mergeMap(action => {
      if (action.type !== FETCH_PERFORMANCE_DATA_SUCCESS) return of();
      
      const { overallResponse, lobResponse } = action.payload;
      return of(
        setOverallPerformance(overallResponse),
        setLOBPerformance(lobResponse),
        setLoading(false),
        setError(null)
      );
    })
  );

// Handle failure
export const handlePerformanceDataFailureEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    mergeMap(action => {
      if (action.type !== FETCH_PERFORMANCE_DATA_FAILURE) return of();
      
      return of(
        setError(action.payload),
        setLoading(false)
      );
    })
  );

// Region selection epic
export const handleRegionSelectEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    mergeMap(action => {
      if (action.type !== setSelectedRegion.type) return of();
      
      return of(
        resetMatrixStates(),
        fetchPerformanceData()
      );
    })
  );

// Filter change epic
export const handleFilterChangeEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      if (action.type !== setCurrentFilters.type) return of();
      
      // Only trigger fetch if the filters have actually changed and this is not the initial state
      const currentFilters = action.payload;
      const previousFilters = state.filters.currentFilters;
      
      // Skip if this is the initial state (previousFilters is undefined)
      if (!previousFilters) return of();
      
      // Skip if filters haven't changed
      if (JSON.stringify(currentFilters) === JSON.stringify(previousFilters)) {
        return of();
      }
      
      return of(fetchPerformanceData(currentFilters));
    })
  );

// Channel Performance Breakdown Epic
export const fetchChannelPerformanceBreakdownEpic: Epic<any, any, RootState> = (action$, state$) => 
  action$.pipe(
    filter((action: any) => action.type === FETCH_CHANNEL_PERFORMANCE_BREAKDOWN_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchChannelPerformanceBreakdown(completeFilters)).pipe(
        map(response => fetchChannelPerformanceBreakdownSuccess(response)),
        catchError(error => of(fetchChannelPerformanceBreakdownFailure(error.message || 'Failed to fetch channel performance breakdown')))
      );
    })
  );

// Handle Channel Performance Breakdown Success
export const handleChannelPerformanceBreakdownSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/fetchChannelPerformanceBreakdownSuccess'),
    mergeMap((action: any) => {
      return of(
        setChannelPerformanceBreakdown(action.payload),
        setChannelPerformanceBreakdownLoading(false),
        setChannelPerformanceBreakdownError(null)
      );
    })
  );

// Handle Channel Performance Breakdown Failure
export const handleChannelPerformanceBreakdownFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/fetchChannelPerformanceBreakdownFailure'),
    mergeMap((action: any) => {
      return of(
        setChannelPerformanceBreakdownError(action.payload),
        setChannelPerformanceBreakdownLoading(false)
      );
    })
  );

// SubChannel Performance Breakdown Epic
export const fetchSubchannelPerformanceBreakdownEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_SUBCHANNEL_PERFORMANCE_BREAKDOWN_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchSubchannelPerformanceBreakdown(completeFilters)).pipe(
        map(response => fetchSubchannelPerformanceBreakdownSuccess(response)),
        catchError(error => of(fetchSubchannelPerformanceBreakdownFailure(error.message || 'Failed to fetch subchannel performance breakdown')))
      );
    })
  );

// Handle SubChannel Performance Breakdown Success
export const handleSubchannelPerformanceBreakdownSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === FETCH_SUBCHANNEL_PERFORMANCE_BREAKDOWN_SUCCESS),
    mergeMap((action: any) => {
      return of(
        setSubchannelPerformanceBreakdown(action.payload),
        setSubchannelPerformanceBreakdownLoading(false),
        setSubchannelPerformanceBreakdownError(null)
      );
    })
  );

// Handle SubChannel Performance Breakdown Failure
export const handleSubchannelPerformanceBreakdownFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === FETCH_SUBCHANNEL_PERFORMANCE_BREAKDOWN_FAILURE),
    mergeMap((action: any) => {
      return of(
        setSubchannelPerformanceBreakdownError(action.payload),
        setSubchannelPerformanceBreakdownLoading(false)
      );
    })
  );

// Product Performance Breakdown Epic
export const fetchProductPerformanceBreakdownEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_PRODUCT_PERFORMANCE_BREAKDOWN_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchProductPerformanceBreakdown(completeFilters)).pipe(
        map(response => fetchProductPerformanceBreakdownSuccess(response)),
        catchError(error => of(fetchProductPerformanceBreakdownFailure(error.message || 'Failed to fetch product performance breakdown')))
      );
    })
  );

// Handle Product Performance Breakdown Success
export const handleProductPerformanceBreakdownSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === FETCH_PRODUCT_PERFORMANCE_BREAKDOWN_SUCCESS),
    mergeMap((action: any) => {
      return of(
        setProductPerformanceBreakdown(action.payload),
        setProductPerformanceBreakdownLoading(false),
        setProductPerformanceBreakdownError(null)
      );
    })
  );

// Handle Product Performance Breakdown Failure
export const handleProductPerformanceBreakdownFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === FETCH_PRODUCT_PERFORMANCE_BREAKDOWN_FAILURE),
    mergeMap((action: any) => {
      return of(
        setProductPerformanceBreakdownError(action.payload),
        setProductPerformanceBreakdownLoading(false)
      );
    })
  );

// Product Channel Distribution epics
export const fetchProductChannelDistributionEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_PRODUCT_CHANNEL_DISTRIBUTION_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchProductChannelDistribution(true, completeFilters)).pipe(
        map((response: ProductChannelDistributionResponse) => fetchProductChannelDistributionSuccess(response)),
        catchError(error => of(fetchProductChannelDistributionFailure(error.message)))
      );
    })
  );

// Channel Product Distribution epics
export const fetchChannelProductDistributionEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_CHANNEL_PRODUCT_DISTRIBUTION_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchChannelProductDistribution(true, completeFilters)).pipe(
        map((response: ChannelProductDistributionResponse) => fetchChannelProductDistributionSuccess(response)),
        catchError(error => of(fetchChannelProductDistributionFailure(error.message)))
      );
    })
  );

// Add success and failure handling epics for product channel and channel product distribution

// Handle Product Channel Distribution Success
export const handleProductChannelDistributionSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_PRODUCT_CHANNEL_DISTRIBUTION_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setProductChannelDistribution(action.payload),
        setProductChannelDistributionLoading(false),
        setProductChannelDistributionError(null)
      );
    })
  );

// Handle Product Channel Distribution Failure
export const handleProductChannelDistributionFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_PRODUCT_CHANNEL_DISTRIBUTION_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setProductChannelDistributionError(action.payload),
        setProductChannelDistributionLoading(false)
      );
    })
  );

// Handle Channel Product Distribution Success
export const handleChannelProductDistributionSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_CHANNEL_PRODUCT_DISTRIBUTION_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setChannelProductDistribution(action.payload),
        setChannelProductDistributionLoading(false),
        setChannelProductDistributionError(null)
      );
    })
  );

// Handle Channel Product Distribution Failure
export const handleChannelProductDistributionFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_CHANNEL_PRODUCT_DISTRIBUTION_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setChannelProductDistributionError(action.payload),
        setChannelProductDistributionLoading(false)
      );
    })
  );

// Add Product Status Distribution epics
export const fetchProductStatusDistributionEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_PRODUCT_STATUS_DISTRIBUTION_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchProductStatusDistribution(true, completeFilters)).pipe(
        map((response: ProductStatusResponse) => fetchProductStatusDistributionSuccess(response)),
        catchError(error => of(fetchProductStatusDistributionFailure(error.message)))
      );
    })
  );

// Handle Product Status Distribution Success
export const handleProductStatusDistributionSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_PRODUCT_STATUS_DISTRIBUTION_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setProductStatusDistribution(action.payload),
        setProductStatusDistributionLoading(false),
        setProductStatusDistributionError(null)
      );
    })
  );

// Handle Product Status Distribution Failure
export const handleProductStatusDistributionFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_PRODUCT_STATUS_DISTRIBUTION_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setProductStatusDistributionError(action.payload),
        setProductStatusDistributionLoading(false)
      );
    })
  );

// Add Channel Status Distribution epics
export const fetchChannelStatusDistributionEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_CHANNEL_STATUS_DISTRIBUTION_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchChannelStatusDistribution(true, completeFilters)).pipe(
        map((response: ChannelStatusResponse) => fetchChannelStatusDistributionSuccess(response)),
        catchError(error => of(fetchChannelStatusDistributionFailure(error.message)))
      );
    })
  );

// Handle Channel Status Distribution Success
export const handleChannelStatusDistributionSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_CHANNEL_STATUS_DISTRIBUTION_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setChannelStatusDistribution(action.payload),
        setChannelStatusDistributionLoading(false),
        setChannelStatusDistributionError(null)
      );
    })
  );

// Handle Channel Status Distribution Failure
export const handleChannelStatusDistributionFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_CHANNEL_STATUS_DISTRIBUTION_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setChannelStatusDistributionError(action.payload),
        setChannelStatusDistributionLoading(false)
      );
    })
  );

// Add Subchannel Status Distribution epics
export const fetchSubchannelStatusDistributionEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_SUBCHANNEL_STATUS_DISTRIBUTION_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchSubchannelStatusDistribution(true, completeFilters)).pipe(
        map((response: SubchannelStatusResponse) => fetchSubchannelStatusDistributionSuccess(response)),
        catchError(error => of(fetchSubchannelStatusDistributionFailure(error.message)))
      );
    })
  );

// Handle Subchannel Status Distribution Success
export const handleSubchannelStatusDistributionSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_SUBCHANNEL_STATUS_DISTRIBUTION_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setSubchannelStatusDistribution(action.payload),
        setSubchannelStatusDistributionLoading(false),
        setSubchannelStatusDistributionError(null)
      );
    })
  );

// Handle Subchannel Status Distribution Failure
export const handleSubchannelStatusDistributionFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_SUBCHANNEL_STATUS_DISTRIBUTION_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setSubchannelStatusDistributionError(action.payload),
        setSubchannelStatusDistributionLoading(false)
      );
    })
  );

// Add Product Conversion % epics
export const fetchProductConversionRateEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_PRODUCT_CONVERSION_RATE_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchProductConversionRate(true, completeFilters)).pipe(
        map((response: ProductConversionResponse) => {
          return fetchProductConversionRateSuccess(response);
        }),
        catchError(error => {
          console.error('Product conversion rate API error:', error);
          return of(fetchProductConversionRateFailure(error.message));
        })
      );
    })
  );

// Add Channel Conversion % epics
export const fetchChannelConversionRateEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_CHANNEL_CONVERSION_RATE_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchChannelConversionRate(true, completeFilters)).pipe(
        map((response: ChannelConversionResponse) => fetchChannelConversionRateSuccess(response)),
        catchError(error => of(fetchChannelConversionRateFailure(error.message)))
      );
    })
  );

// Handle Channel Conversion % Success
export const handleChannelConversionRateSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_CHANNEL_CONVERSION_RATE_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setChannelConversionRate(action.payload),
        setChannelConversionRateLoading(false),
        setChannelConversionRateError(null)
      );
    })
  );

// Handle Channel Conversion % Failure
export const handleChannelConversionRateFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_CHANNEL_CONVERSION_RATE_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setChannelConversionRateError(action.payload),
        setChannelConversionRateLoading(false)
      );
    })
  );

// Add Subchannel Conversion % epics
export const fetchSubchannelConversionRateEpic: Epic<any, any, RootState> = (action$, state$) =>
  action$.pipe(
    filter((action: any) => action.type === FETCH_SUBCHANNEL_CONVERSION_RATE_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const filters = state.filters.currentFilters;
      
      // Create a complete SelectedFilters object with dateRange
      const completeFilters = {
        ...filters,
        dateRange: { startDate: null, endDate: null },
        ...action.payload // In case filters were passed with the action
      };
      
      return from(fetchSubchannelConversionRate(true, completeFilters)).pipe(
        map((response: SubchannelConversionResponse) => fetchSubchannelConversionRateSuccess(response)),
        catchError(error => of(fetchSubchannelConversionRateFailure(error.message)))
      );
    })
  );

// Handle Subchannel Conversion % Success
export const handleSubchannelConversionRateSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_SUBCHANNEL_CONVERSION_RATE_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setSubchannelConversionRate(action.payload),
        setSubchannelConversionRateLoading(false),
        setSubchannelConversionRateError(null)
      );
    })
  );

// Handle Subchannel Conversion % Failure
export const handleSubchannelConversionRateFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_SUBCHANNEL_CONVERSION_RATE_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setSubchannelConversionRateError(action.payload),
        setSubchannelConversionRateLoading(false)
      );
    })
  );

// Handle Product Conversion % Success
export const handleProductConversionRateSuccessEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_PRODUCT_CONVERSION_RATE_SUCCESS'),
    mergeMap((action: any) => {
      return of(
        setProductConversionRate(action.payload),
        setProductConversionRateLoading(false),
        setProductConversionRateError(null)
      );
    })
  );

// Handle Product Conversion % Failure
export const handleProductConversionRateFailureEpic: Epic<any, any, RootState> = (action$) => 
  action$.pipe(
    filter((action: any) => action.type === 'dashboard/FETCH_PRODUCT_CONVERSION_RATE_FAILURE'),
    mergeMap((action: any) => {
      return of(
        setProductConversionRateError(action.payload),
        setProductConversionRateLoading(false)
      );
    })
  );

export default [
  fetchPerformanceDataEpic,
  handlePerformanceDataSuccessEpic,
  handlePerformanceDataFailureEpic,
  handleRegionSelectEpic,
  handleFilterChangeEpic,
  fetchChannelPerformanceBreakdownEpic,
  handleChannelPerformanceBreakdownSuccessEpic,
  handleChannelPerformanceBreakdownFailureEpic,
  fetchSubchannelPerformanceBreakdownEpic,
  handleSubchannelPerformanceBreakdownSuccessEpic,
  handleSubchannelPerformanceBreakdownFailureEpic,
  fetchProductPerformanceBreakdownEpic,
  handleProductPerformanceBreakdownSuccessEpic,
  handleProductPerformanceBreakdownFailureEpic,
  fetchProductChannelDistributionEpic,
  handleProductChannelDistributionSuccessEpic,
  handleProductChannelDistributionFailureEpic,
  fetchChannelProductDistributionEpic,
  handleChannelProductDistributionSuccessEpic,
  handleChannelProductDistributionFailureEpic,
  fetchProductStatusDistributionEpic,
  handleProductStatusDistributionSuccessEpic,
  handleProductStatusDistributionFailureEpic,
  fetchChannelStatusDistributionEpic,
  handleChannelStatusDistributionSuccessEpic,
  handleChannelStatusDistributionFailureEpic,
  fetchSubchannelStatusDistributionEpic,
  handleSubchannelStatusDistributionSuccessEpic,
  handleSubchannelStatusDistributionFailureEpic,
  fetchProductConversionRateEpic,
  handleProductConversionRateSuccessEpic,
  handleProductConversionRateFailureEpic,
  fetchChannelConversionRateEpic,
  handleChannelConversionRateSuccessEpic,
  handleChannelConversionRateFailureEpic,
  fetchSubchannelConversionRateEpic,
  handleSubchannelConversionRateSuccessEpic,
  handleSubchannelConversionRateFailureEpic
]; 